import { IDataWithLocalizedName } from '../../contracts/localized-data.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { LocalizedString } from './localized-string.js';
import { MapLocation } from './map-location.js';

export enum PlaceStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export enum PlaceDisplayMode {
  // On UI the name, locality and address parameters will be shown.
  Precise = 'Precise',

  // On UI the name and address parameters will be shown.
  Approximate = 'Approximate',

  // On UI only the name parameter will be shown.
  Minimal = 'Minimal',
}

/**
 * @deprecated Use LocationComponent for event locations. Business places are legacy data.
 */
export class Place extends DataModel implements IDataWithLocalizedName {
  static readonly TYPE_ID: string = '5c0d1bb1509a4e55867d7a09684bc3cb';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly status: PlaceStatus,
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly names: LocalizedString,
    public readonly mapLocation: MapLocation,
    public readonly displayMode: PlaceDisplayMode,
  ) {
    super();
  }

  get $typeId(): string {
    return Place.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, Place.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): Place {
    return new Place(
        toEnumFromString(MapUtils.get<string>(map, '$status'), PlaceStatus),
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData(MapUtils.get<SerializedData>(map, 'names')),
        context.fromData(MapUtils.get<SerializedData>(map, 'mapLocation')),
        toEnumFromString(MapUtils.get<string>(map, 'displayMode'), PlaceDisplayMode),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'names': context.toData(this.names),
      'mapLocation': context.toData(this.mapLocation),
      'displayMode': fromEnumAsString(this.displayMode),
    };
  }

  clone(): this {
    return new Place(
        this.status,
        this.uniqueId,
        this.businessRef.clone(),
        this.names.clone(),
        this.mapLocation.clone(),
        this.displayMode,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.status === this.status &&
        other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.names, this.names) &&
        isSameData(other.mapLocation, this.mapLocation) &&
        other.displayMode === this.displayMode;
  }

  copyWith(data: Partial<Place>): this {
    return new Place(
        data.status ?? this.status,
        data.uniqueId ?? this.uniqueId,
        data.businessRef ?? this.businessRef.clone(),
        data.names ?? this.names.clone(),
        data.mapLocation ?? this.mapLocation.clone(),
        data.displayMode ?? this.displayMode,
    ) as this;
  }
}

export class PlaceBuilder extends DataModelBuilder<Place> {
  build(context: SerializationContext, map: SerializedData): Place {
    return Place.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, 'displayMode', PlaceDisplayMode, PlaceDisplayMode.Precise);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Place.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Place.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `displayMode` parameter of type `PlaceDisplayMode` was introduced.
    if(version < 2) {
      map['displayMode'] ??= fromEnumAsString(PlaceDisplayMode.Precise);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
